import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Loader2, CheckCircle2, Download, AlertCircle, KeyRound, MessageCircle } from "lucide-react";
import axios from "axios";
import { getCheckoutStatus, downloadPdfUrl } from "../lib/api";
import { Tx } from "../context/AutoTranslateContext";
import WhatsAppOptIn from "../components/WhatsAppOptIn";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NEEDS_LOGIN = new Set([
  "academy_subscription", "juz_certificate", "ijaza_certificate",
  "tutor_session", "diploma_course",
]);

const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling"); // polling | paid | failed | expired
  const [data, setData] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [setupLink, setSetupLink] = useState(null);
  const [setupPhoneUsed, setSetupPhoneUsed] = useState(false);
  const [setupError, setSetupError] = useState("");
  const navigate = useNavigate();
  const pollingRef = useRef(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }

    let cancelled = false;

    const poll = async (n) => {
      if (cancelled) return;
      try {
        const res = await getCheckoutStatus(sessionId);
        setData(res);
        if (res.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (res.status === "expired") {
          setStatus("expired");
          return;
        }
        // Keep polling up to ~3 minutes — webhook usually arrives within seconds
        if (n >= 72) {
          setStatus("failed");
          return;
        }
        setAttempts(n + 1);
        pollingRef.current = setTimeout(() => poll(n + 1), 2500);
      } catch (e) {
        if (n >= 30) {
          setStatus("failed");
          return;
        }
        pollingRef.current = setTimeout(() => poll(n + 1), 3000);
      }
    };

    poll(0);

    return () => {
      cancelled = true;
      if (pollingRef.current) clearTimeout(pollingRef.current);
    };
  }, [sessionId]);

  // Once paid — if the product requires login access, fetch the buyer's
  // set-password link so we can render it directly on this page (independent
  // of the email actually landing).
  useEffect(() => {
    if (status !== "paid" || !sessionId || !data) return;
    if (!NEEDS_LOGIN.has(data.product_type)) return;
    axios.get(`${API}/checkout/setup-account/${sessionId}`)
      .then((r) => { setSetupLink(r.data.reset_link); setSetupPhoneUsed(!!r.data.phone_used); })
      .catch((e) => setSetupError(e.response?.data?.detail || e.message || "Could not create your login link"));
  }, [status, sessionId, data]);

  return (
    <div data-testid="payment-success-page" className="max-w-2xl mx-auto px-4 sm:px-8 py-16 sm:py-24">
      <div className="clay-surface rounded-3xl p-8 sm:p-12 text-center">
        {status === "polling" && (
          <div data-testid="payment-polling">
            <Loader2 className="w-12 h-12 text-forest animate-spin mx-auto mb-5" />
            <h1 className="font-heading text-3xl text-forest font-semibold mb-2">
              <Tx>Verifying your payment</Tx>
            </h1>
            <p className="text-mute leading-relaxed">
              <Tx>Please wait a moment while we confirm the transaction with Stripe.</Tx>
              <br />
              <span className="text-xs"><Tx>Attempt</Tx> {attempts + 1}…</span>
            </p>
          </div>
        )}

        {status === "paid" && (
          <div data-testid="payment-paid">
            <CheckCircle2 className="w-16 h-16 text-forest mx-auto mb-5" />
            <h1 className="font-heading text-3xl sm:text-4xl text-forest font-semibold mb-3">
              <Tx>JazakAllahu Khairan!</Tx>
            </h1>
            <p className="text-mute leading-relaxed mb-8">
              {data?.product_type === "exam_booking"
                ? <Tx>Your exam booking is confirmed. Our team will contact you on WhatsApp to schedule your 30-minute video exam.</Tx>
                : data?.product_type === "diploma_certificate"
                ? <Tx>Your certificate purchase is confirmed. Sheikh Amar will sign and stamp your diploma, and we'll email the signed PDF within 3–5 business days.</Tx>
                : data?.product_type === "shop_item"
                ? <Tx>Your order is confirmed. We'll ship it within 2–3 business days and email you the tracking details.</Tx>
                : data?.product_type === "academy_subscription"
                ? <Tx>Your Academy Pass is now active! You have 30 days of unlimited access to the Colored Mushaf, all reciters, Tafsir, Arabic curriculum and AI Pronunciation Coach. If you don't already have an account, create one below with the SAME email you paid with — it will unlock automatically.</Tx>
                : data?.product_type === "juz_certificate"
                ? <Tx>Your Juz' Completion Certificate order is confirmed. Our team will WhatsApp you to schedule your 30-minute live recitation review with an approved qārī. The signed certificate PDF will be emailed to you after the review.</Tx>
                : data?.product_type === "ijaza_certificate"
                ? <Tx>Your full Ijāza Certificate order is confirmed. Our team will WhatsApp you to schedule your assessment. The signed & sanad-stamped certificate will be emailed to you after completion.</Tx>
                : data?.product_type === "tutor_session"
                ? <Tx>Your live tutoring booking is confirmed! The video link and session details have been emailed to you. Your tutor and our admin have also been notified on WhatsApp.</Tx>
                : <Tx>Your payment was successful. Your protocol PDF is ready to download.</Tx>}
            </p>

            {/* Direct WhatsApp confirmation — opens the buyer's WhatsApp pre-filled
                with their order details so the clinic ALWAYS receives it directly,
                independent of the automated notification. Shown for every paid order. */}
            {data?.whatsapp_confirm_text && data?.clinic_whatsapp && (
              <div className="mb-8">
                <a
                  data-testid="confirm-on-whatsapp-btn"
                  href={`https://wa.me/${data.clinic_whatsapp.replace("+", "")}?text=${encodeURIComponent(data.whatsapp_confirm_text)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2.5 bg-[#25D366] text-white hover:bg-[#20bd5a] rounded-full px-8 py-4 text-sm font-semibold transition-all hover:-translate-y-0.5 shadow-lg shadow-[#25D366]/30"
                >
                  <MessageCircle className="w-5 h-5" />
                  <Tx>Confirm my order on WhatsApp</Tx>
                </a>
                <p className="text-[11px] text-mute mt-2">
                  <Tx>Tap to send your booking details to the clinic on WhatsApp so we can confirm right away.</Tx>
                </p>
              </div>
            )}
            {data?.download_token && !["exam_booking", "diploma_certificate", "shop_item", "academy_subscription", "juz_certificate", "ijaza_certificate", "tutor_session"].includes(data?.product_type) && (
              <a
                href={downloadPdfUrl(data.download_token, data.product_type)}
                data-testid="download-pdf-link"
                className="inline-flex items-center gap-2 bg-forest text-sand hover:bg-sage rounded-full px-8 py-4 text-sm font-medium transition-all hover:-translate-y-0.5 shadow-lg shadow-forest/20"
              >
                <Download className="w-4 h-4" />
                {data.product_type === "diploma_course"
                  ? <Tx>Download my Course PDF</Tx>
                  : data.product_type === "hijama_atlas_book"
                  ? <Tx>Download my Hijama Atlas Book</Tx>
                  : <Tx>Download my PDF</Tx>}
              </a>
            )}
            {data?.product_type === "academy_subscription" && (
              <div className="space-y-3">
                <Link to="/academy" data-testid="pass-open-academy"
                  className="inline-flex items-center gap-2 bg-forest text-sand hover:bg-sage rounded-full px-8 py-4 text-sm font-medium transition-all shadow-lg shadow-forest/20">
                  <Tx>Open the Academy →</Tx>
                </Link>
              </div>
            )}
            {(data?.product_type === "juz_certificate" || data?.product_type === "ijaza_certificate") && (
              <Link to="/academy/tutors" data-testid="cert-open-tutors"
                className="inline-flex items-center gap-2 bg-forest text-sand hover:bg-sage rounded-full px-8 py-4 text-sm font-medium transition-all shadow-lg shadow-forest/20">
                <Tx>Meet our tutors →</Tx>
              </Link>
            )}
            {data?.product_type === "tutor_session" && data?.tutor_session_id && (
              <Link to={`/academy/session/${data.tutor_session_id}`} data-testid="session-open-live"
                className="inline-flex items-center gap-2 bg-forest text-sand hover:bg-sage rounded-full px-8 py-4 text-sm font-medium transition-all shadow-lg shadow-forest/20">
                <Tx>Open my live session →</Tx>
              </Link>
            )}

            {data?.product_type === "tutor_session" && <WhatsAppOptIn />}

            {/* SET-PASSWORD CARD — always shown for login-required purchases */}
            {NEEDS_LOGIN.has(data?.product_type) && (
              <div data-testid="setup-account-card" className="mt-8 rounded-3xl border-2 border-gold/40 bg-gold/10 p-6 text-left">
                <div className="flex items-start gap-3 mb-3">
                  <KeyRound className="w-6 h-6 text-gold flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-heading text-lg text-forest font-semibold mb-1">
                      <Tx>Set your password to sign in anytime</Tx>
                    </h3>
                    <p className="text-xs text-mute leading-relaxed">
                      <Tx>Your account is linked to</Tx>{" "}
                      <strong className="text-forest">{data?.full_name ? data.full_name : ""}</strong>{" "}
                      <Tx>at</Tx> <strong className="text-forest">{data && (data.metadata?.email || data.email) || ""}</strong>.
                      <br/>
                      <Tx>Click the button below now to choose your password — the link is valid for 14 days.</Tx>
                    </p>
                  </div>
                </div>
                {setupLink ? (
                  <>
                    <a
                      href={setupLink}
                      data-testid="setup-password-btn"
                      className="inline-flex items-center gap-2 bg-forest text-sand hover:bg-sage rounded-full px-6 py-3 text-sm font-medium transition"
                    >
                      <KeyRound className="w-4 h-4" /> <Tx>Set my password now</Tx>
                    </a>
                    {setupPhoneUsed && (
                      <p className="text-[11px] text-mute italic mt-3">
                        ✓ <Tx>We also sent this link to your WhatsApp for safekeeping.</Tx>
                      </p>
                    )}
                    <p className="text-[11px] text-mute italic mt-2">
                      <Tx>Prefer email? Check your inbox — the same link was sent there too.</Tx>
                    </p>
                  </>
                ) : setupError ? (
                  <p className="text-xs text-clay bg-clay/10 rounded-2xl p-3">{setupError}</p>
                ) : (
                  <p className="text-xs text-mute italic"><Loader2 className="w-3 h-3 animate-spin inline mr-1" /> <Tx>Preparing your login link…</Tx></p>
                )}
              </div>
            )}
            {data?.product_type === "exam_booking" && (
              <div data-testid="exam-booking-banner" className="rounded-2xl bg-forest/5 border border-forest/20 p-5 text-left mt-4">
                <p className="text-xs uppercase tracking-widest text-forest mb-2 font-medium">📞 <Tx>Next step</Tx></p>
                <p className="text-sm text-ink leading-relaxed">
                  <Tx>We'll WhatsApp you within 24 hours to confirm a time for your 30-minute practical exam. Please ensure you've studied the course PDF and have practised on real patients with a qualified Hijama practitioner.</Tx>
                </p>
              </div>
            )}
            <p className="text-xs text-mute mt-6">
              <Tx>You can re-download anytime from this link. Keep it safe.</Tx>
            </p>
            {data?.certificate_pending && (
              <div data-testid="certificate-pending-banner" className="mt-8 rounded-2xl bg-gold/10 border border-gold/30 p-5 text-left">
                <p className="text-xs uppercase tracking-widest text-gold mb-2 font-medium">
                  📜 <Tx>Your certificate is being prepared</Tx>
                </p>
                <p className="text-sm text-ink leading-relaxed">
                  <Tx>Your Hijama Diploma certificate for</Tx>{" "}
                  <strong className="text-forest">{data.full_name || "your enrolment"}</strong>{" "}
                  <Tx>is being prepared. Once Sheikh Amar has personally signed and stamped it, we will email the official certificate to you within 3–5 business days.</Tx>
                </p>
              </div>
            )}
            <div className="mt-8 pt-6 border-t border-forest/10">
              <Link
                to={data?.illness_id ? `/illness/${data.illness_id}` : "/"}
                className="text-sm text-forest hover:underline"
              >
                ← <Tx>Back to the home page</Tx>
              </Link>
            </div>
          </div>
        )}

        {(status === "failed" || status === "expired") && (
          <div data-testid="payment-failed">
            <AlertCircle className="w-16 h-16 text-clay mx-auto mb-5" />
            <h1 className="font-heading text-3xl text-forest font-semibold mb-3">
              {status === "expired" ? <Tx>Session expired</Tx> : <Tx>Payment not confirmed</Tx>}
            </h1>
            <p className="text-mute leading-relaxed mb-8">
              {status === "expired"
                ? <Tx>Your checkout session has expired. Please try again.</Tx>
                : <Tx>We could not confirm your payment yet. If you were charged, contact us with your session ID.</Tx>}
            </p>
            <button
              onClick={() => navigate(-1)}
              className="bg-forest text-sand hover:bg-sage rounded-full px-7 py-3.5 text-sm font-medium transition"
            >
              <Tx>Try again</Tx>
            </button>
            <p className="text-[11px] text-mute mt-6 break-all">
              <Tx>Session</Tx>: {sessionId || "(none)"}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
